import {
    Avatar,
    Box,
    Card,
    Chip,
    CircularProgress,
    IconButton,
    Stack,
    Tooltip,
    Typography,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import PersonIcon from "@mui/icons-material/Person";
import InfoIcon from "@mui/icons-material/Info";
import SentimentVerySatisfiedIcon from "@mui/icons-material/SentimentVerySatisfied";
import SentimentSatisfiedIcon from "@mui/icons-material/SentimentSatisfied";
import SentimentDissatisfiedIcon from "@mui/icons-material/SentimentDissatisfied";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CancelIcon from "@mui/icons-material/Cancel";
import { Player } from "../api/types.tsx";

interface PlayerGridProps {
    players: Player[];
    selectedIds: number[];
    topPlayers: number[];
    loading: boolean;
    onToggle: (id: number) => void;
    onDetail: (p: Player) => void;
    onCertificateEdit: (p: Player) => void;
    initials: (p: Player) => string;
}

const getSkillColor = (skill: number) => {
    if (skill < 80) return "#90CAF9";
    if (skill < 84) return "#A5D6A7";
    if (skill < 88) return "#FFE082";
    if (skill < 92) return "#FFCC80";
    return "#EF9A9A";
};

const getCertificateStatus = (dateStr?: string) => {
    if (!dateStr) return { icon: <CancelIcon color="disabled" /> };
    const expiry = new Date(dateStr);
    const now = new Date();
    const nextMonth = new Date();
    nextMonth.setMonth(now.getMonth() + 1);
    if (expiry < now) return { icon: <CancelIcon sx={{ color: "error.main" }} /> };
    if (expiry <= nextMonth) return { icon: <WarningAmberIcon sx={{ color: "warning.main" }} /> };
    return { icon: <CheckCircleIcon sx={{ color: "success.main" }} /> };
};

const getSkillDifferenceIcon = (baseSkill: number, actualSkill: number) => {
    const diff = actualSkill - baseSkill;
    if (diff <= 0) return <SentimentDissatisfiedIcon sx={{ color: "#e53935", fontSize: "1.25rem" }} />;
    if (diff === 1) return <SentimentSatisfiedIcon sx={{ color: "#fb8c00", fontSize: "1.25rem" }} />;
    if (diff === 2) return <SentimentSatisfiedIcon sx={{ color: "#43a047", fontSize: "1.25rem" }} />;
    return <SentimentVerySatisfiedIcon sx={{ color: "#43a047", fontSize: "1.25rem" }} />;
};

export function PlayerGrid({ players, selectedIds, topPlayers, loading, onToggle, onDetail, onCertificateEdit, initials }: PlayerGridProps) {
    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        // griglia che si adatta alla larghezza disponibile (metà pagina su desktop)
        <Box
            sx={{
                display: "grid",
                gridTemplateColumns: {
                    xs: "repeat(auto-fill, minmax(150px, 1fr))",
                    sm: "repeat(auto-fill, minmax(122px, 1fr))",
                },
                gap: 1.25,
            }}
        >
            {players.map((p) => {
                const checked = selectedIds.includes(p.id);
                return (
                        <Card
                            key={p.id}
                            onClick={() => onToggle(p.id)}
                            elevation={checked ? 6 : 1}
                            sx={{
                                p: 0.8,
                                textAlign: "center",
                                height: 112,
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "space-between",
                                borderRadius: 3,
                                border: checked ? "2px solid" : "1px solid",
                                borderColor: checked ? "primary.main" : "divider",
                                transition: "all 0.15s ease",
                                background: "#fff",
                                "&:hover": { transform: "scale(1.02)" },
                            }}
                        >
                            <Avatar
                                sx={{
                                    width: 30,
                                    height: 30,
                                    fontSize: "0.85rem",
                                    mx: "auto",
                                    mb: 0.3,
                                    bgcolor: p.gender === "M" ? "info.main" : "secondary.light",
                                    color: "white",
                                    fontWeight: 350,
                                }}
                            >
                                {initials(p)}
                            </Avatar>

                            <Stack direction="row" spacing={0.5} justifyContent="center" alignItems="center">
                                <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                                >
                                    {p.shirtNumber} - {p.nickname}
                                </Typography>
                                {getSkillDifferenceIcon(p.baseSkill, p.skill)}
                            </Stack>

                            <Chip
                                label={`SK ${p.skill.toFixed(1)}`}
                                size="small"
                                sx={{ mt: 0.3, mx: "auto", height: 20, fontSize: "0.7rem", color: "#000", backgroundColor: getSkillColor(p.skill), fontWeight: 600 }}
                            />

                            <Stack direction="row" justifyContent="center" alignItems="center" spacing={0.5} mt={0.3}>
                                {p.suitabilityCertificate && (
                                    <Tooltip title={`Certificato: ${new Date(p.suitabilityCertificate).toLocaleDateString("it-IT")}`}>
                                        <Box
                                            display="flex"
                                            justifyContent="center"
                                            alignItems="center"
                                            onDoubleClick={(e) => {
                                                e.stopPropagation();
                                                onCertificateEdit(p);
                                            }}
                                            sx={{ cursor: "pointer" }}
                                        >
                                            {getCertificateStatus(p.suitabilityCertificate).icon}
                                        </Box>
                                    </Tooltip>
                                )}

                                <Tooltip title={checked ? "Deselect" : "Select"}>
                                    <IconButton
                                        color={checked ? "primary" : "default"}
                                        size="small"
                                        sx={{ p: 0.4 }}
                                        onClick={(e) => { e.stopPropagation(); onToggle(p.id); }}
                                    >
                                        {checked ? <CheckIcon fontSize="small" /> : <PersonIcon fontSize="small" />}
                                    </IconButton>
                                </Tooltip>

                                <Tooltip title="Dettagli">
                                    <IconButton
                                        size="small"
                                        color="secondary"
                                        sx={{ p: 0.4 }}
                                        onClick={(e) => { e.stopPropagation(); onDetail(p); }}
                                    >
                                        <InfoIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>

                                {(() => {
                                    const idx = topPlayers.indexOf(p.id);
                                    if (idx === -1) return null;
                                    const starsCount = 3 - idx;
                                    return (
                                        <Tooltip title="Top Performer">
                                            <Stack direction="row" spacing={0.1} justifyContent="center">
                                                {[...Array(starsCount)].map((_, i) => (
                                                    <Typography key={i} sx={{ color: "#2600ffff", fontSize: "0.8rem" }}>★</Typography>
                                                ))}
                                            </Stack>
                                        </Tooltip>
                                    );
                                })()}
                            </Stack>
                        </Card>
                );
            })}
        </Box>
    );
}
